'use client';

import ProductCard from '@/features/product-card/ProductCard';
import {
  specCardVariants,
  specRowVariants,
  staggerContainer,
} from '@/features/product/motion/productMotion';
import { motion, useReducedMotion } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import {
  A11y,
  Autoplay,
  EffectCoverflow,
  Keyboard,
  Mousewheel,
  Navigation,
  Pagination,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import './relatedCarousel.scss';

function readMessage(messages, path, fallback = '') {
  const resolved = path
    .split('.')
    .reduce((current, segment) => current?.[segment], messages);

  return typeof resolved === 'string' ? resolved : fallback;
}

function fillTemplate(template, replacements = {}) {
  return Object.entries(replacements).reduce(
    (value, [key, replacement]) =>
      value.replaceAll(`{{${key}}}`, String(replacement)),
    String(template || ''),
  );
}

export default function RelatedCarousel({
  activeCoupons = [],
  messages = {},
  products = [],
}) {
  const prefersReducedMotion = useReducedMotion();
  const swiperRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [coverflowCfg, setCoverflowCfg] = useState({
    depth: 180,
    modifier: 1.16,
    rotate: 24,
    scale: 0.9,
    stretch: 0,
  });

  const total = products.length;
  const hasControls = total > 1;
  const canLoop = total > 4;
  const canAutoplay = canLoop && !prefersReducedMotion;

  useEffect(() => {
    const swiper = swiperRef.current;

    if (!swiper || swiper.destroyed) {
      return;
    }

    if (canLoop && typeof swiper.slideToLoop === 'function') {
      swiper.slideToLoop(0, 0);
    } else {
      swiper.slideTo(0, 0);
    }

    swiper.update();
  }, [products]);

  useEffect(() => {
    const swiper = swiperRef.current;

    if (!swiper?.autoplay) {
      return;
    }

    if (isHovered) {
      swiper.autoplay.stop();
      return;
    }

    if (canAutoplay) {
      swiper.autoplay.start();
    }
  }, [canAutoplay, isHovered]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const getCoverflowCfg = () => {
      const width = window.innerWidth;

      if (width < 576) {
        return {
          depth: 88,
          modifier: 1.04,
          rotate: 14,
          scale: 0.94,
          stretch: 0,
        };
      }

      if (width < 992) {
        return {
          depth: 122,
          modifier: 1.08,
          rotate: 18,
          scale: 0.92,
          stretch: 0,
        };
      }

      return {
        depth: 180,
        modifier: 1.16,
        rotate: 24,
        scale: 0.9,
        stretch: 0,
      };
    };

    const syncCoverflowCfg = () => {
      setCoverflowCfg(getCoverflowCfg());
      swiperRef.current?.update?.();
    };

    syncCoverflowCfg();
    window.addEventListener('resize', syncCoverflowCfg);

    return () => {
      window.removeEventListener('resize', syncCoverflowCfg);
    };
  }, []);

  if (!products.length) {
    return null;
  }

  return (
    <motion.section
      id="related-carousel"
      className={`product-related-carousel${
        prefersReducedMotion ? ' reduce-motion' : ''
      }`}
      aria-roledescription="carousel"
      aria-label={readMessage(messages, 'relatedTitleText', 'Related products')}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      variants={prefersReducedMotion ? undefined : specCardVariants}
      initial={prefersReducedMotion ? false : 'hidden'}
      animate={prefersReducedMotion ? undefined : 'visible'}
    >
      <div className="product-related-carousel__shell">
        <motion.div
          className="product-related-carousel__stage"
          variants={prefersReducedMotion ? undefined : staggerContainer}
        >
          <div className="product-related-carousel__viewport">
            <div
              className="product-related-carousel__edge product-related-carousel__edge--left"
              aria-hidden="true"
            />
            <div
              className="product-related-carousel__edge product-related-carousel__edge--right"
              aria-hidden="true"
            />

            <Swiper
              modules={[
                Navigation,
                Pagination,
                Autoplay,
                Keyboard,
                Mousewheel,
                EffectCoverflow,
                A11y,
              ]}
              className="product-related-carousel__swiper"
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              a11y={{ enabled: true }}
              allowTouchMove={hasControls}
              autoplay={
                canAutoplay
                  ? {
                      delay: 480000,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: false,
                    }
                  : false
              }
              centeredSlides
              centeredSlidesBounds={!canLoop}
              coverflowEffect={
                prefersReducedMotion
                  ? undefined
                  : {
                      depth: coverflowCfg.depth,
                      modifier: coverflowCfg.modifier,
                      rotate: coverflowCfg.rotate,
                      scale: coverflowCfg.scale,
                      slideShadows: true,
                      stretch: coverflowCfg.stretch,
                    }
              }
              effect={prefersReducedMotion ? 'slide' : 'coverflow'}
              grabCursor={hasControls}
              keyboard={{ enabled: true, onlyInViewport: true }}
              loop={canLoop}
              mousewheel={
                hasControls && !prefersReducedMotion
                  ? { forceToAxis: true, releaseOnEdges: true }
                  : false
              }
              navigation={
                hasControls
                  ? {
                      nextEl: '.product-related-carousel__nav--next',
                      prevEl: '.product-related-carousel__nav--prev',
                    }
                  : false
              }
              pagination={
                hasControls
                  ? {
                      clickable: true,
                      el: '.product-related-carousel__pagination',
                    }
                  : false
              }
              rewind={!canLoop}
              slideToClickedSlide={hasControls}
              slidesPerView="auto"
              spaceBetween={18}
              speed={prefersReducedMotion ? 380 : 720}
              watchOverflow
              watchSlidesProgress
            >
              {products.map((product, index) => (
                <SwiperSlide
                  key={product.productNumber}
                  className="product-related-carousel__slide"
                  aria-label={fillTemplate(
                    readMessage(
                      messages,
                      'related.slideLabel',
                      'Related product {{index}} of {{total}}',
                    ),
                    { index: index + 1, total },
                  )}
                >
                  <motion.div
                    className="product-related-carousel__card-shell"
                    variants={
                      prefersReducedMotion ? undefined : specRowVariants
                    }
                    custom={index}
                  >
                    <ProductCard
                      product={product}
                      activeCoupons={activeCoupons}
                      disableEntranceMotion
                      showAddToCartButton={false}
                      showCategoryBadge
                      showRatingText={false}
                    />
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {hasControls ? (
            <>
              <Button
                className="product-related-carousel__nav product-related-carousel__nav--prev"
                variant="light"
                aria-label={readMessage(
                  messages,
                  'related.carouselPrev',
                  'Previous related product',
                )}
              >
                <ChevronLeft size={18} aria-hidden="true" />
              </Button>

              <Button
                className="product-related-carousel__nav product-related-carousel__nav--next"
                variant="light"
                aria-label={readMessage(
                  messages,
                  'related.carouselNext',
                  'Next related product',
                )}
              >
                <ChevronRight size={18} aria-hidden="true" />
              </Button>

              <div
                className="product-related-carousel__pagination"
                aria-label={readMessage(
                  messages,
                  'carouselDots',
                  'Related product indicators',
                )}
              />
            </>
          ) : null}
        </motion.div>
        <div className="product-related-carousel__footer">
          <p className="product-related-carousel__hint mb-0">
            {readMessage(
              messages,
              'related.continueHint',
              'Keep exploring similar gear or jump back into the full catalog.',
            )}
          </p>
          <Link
            href="/shop#latest-products"
            className="product-related-carousel__continue app-button app-button--info "
          >
            {readMessage(
              messages,
              'related.continueCta',
              'Browse more products',
            )}
          </Link>
        </div>
      </div>
    </motion.section>
  );
}
